import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sheet } from '../kit/Sheet';
import { Button } from '../kit/Button';
import { SignaturePad } from '../form/SignaturePad';
import { RatingStars } from '../form/RatingStars';
import { PhotoCapture } from '../form/PhotoCapture';
import { formatISTTime } from '../../lib/format';
import { haptic } from '../../lib/haptic';

type ReleaseSignoff =
  | { passengerSignature: string; requesterRating: number; signedOn: string; dutySlipUrl?: string | null }
  | { signatureSkippedReason: string };

/**
 * Customer-facing journey summary for the signature moment. TIMES ONLY —
 * per the 2026-07-02 decision the passenger must never see odometer readings
 * (start/release/end KM), which ops may adjust before billing. The release
 * KM + location are captured on the post-signature close-out screen instead.
 */
type Journey = {
  /** tripMeta.rideStartedAt — when the passenger boarded (ISO). */
  pickupAt: string | null;
  /** Release moment, IST wall-clock HH:mm (captured at slide-to-release). */
  releaseTime: string;
};

type Props = {
  open: boolean;
  bookingId: string;
  mode: 'release' | 'recapture';
  journey: Journey;
  onSubmit: (signoff: ReleaseSignoff) => Promise<void>;
  onClose: () => void;
  onSubmitted: () => void;
};

const SKIP_REASONS = ['Passenger left without signing', 'In a hurry', 'Other — type'] as const;
type SkipReason = (typeof SKIP_REASONS)[number];

export function ReleaseDutySlipSheet({
  open,
  bookingId: _bookingId,
  mode,
  journey,
  onSubmit,
  onClose,
  onSubmitted,
}: Props) {
  const { t } = useTranslation('trip');

  const [signature, setSignature] = useState<string | null>(null);
  const [rating, setRating] = useState(0);
  const [dutySlipUrl, setDutySlipUrl] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Skip modal state
  const [skipOpen, setSkipOpen] = useState(false);
  const [skipReason, setSkipReason] = useState<SkipReason | null>(null);
  const [skipOtherText, setSkipOtherText] = useState('');

  const canSubmitSigned = signature != null && rating > 0;

  const effectiveSkipReason =
    skipReason === 'Other — type' ? skipOtherText.trim() : skipReason ?? '';
  const canConfirmSkip = effectiveSkipReason.length > 0;

  const handleSubmitClick = () => {
    if (canSubmitSigned) {
      void (async () => {
        setSubmitting(true);
        setError(null);
        try {
          await onSubmit({
            passengerSignature: signature,
            requesterRating: rating,
            signedOn: new Date().toISOString(),
            dutySlipUrl: dutySlipUrl || null,
          });
          onSubmitted();
          onClose();
        } catch (e: unknown) {
          haptic.warn();
          const msg = e instanceof Error ? e.message : (e as { message?: string })?.message;
          setError(msg ?? t('submit_failed', { defaultValue: 'Submit failed. Please try again.' }));
        } finally {
          setSubmitting(false);
        }
      })();
    } else if (signature == null && mode === 'release') {
      setSkipOpen(true);
    }
    // else: recapture with no signature or signed but rating=0 → inert
  };

  const handleSkipConfirm = () => {
    if (!canConfirmSkip) return;
    void (async () => {
      setSubmitting(true);
      setError(null);
      try {
        await onSubmit({ signatureSkippedReason: effectiveSkipReason });
        setSkipOpen(false);
        onSubmitted();
        onClose();
      } catch (e: unknown) {
        haptic.warn();
        const msg = e instanceof Error ? e.message : (e as { message?: string })?.message;
        setError(msg ?? t('submit_failed', { defaultValue: 'Submit failed. Please try again.' }));
      } finally {
        setSubmitting(false);
      }
    })();
  };

  const pickupDisplay = journey.pickupAt ? formatISTTime(journey.pickupAt) : '—';
  const releaseDisplay = journey.releaseTime ? journey.releaseTime.slice(0, 5) : '—';

  return (
    <>
      <Sheet
        open={open}
        onClose={onClose}
        title={t('yourJourneyTitle', { defaultValue: 'Your journey' })}
      >
        <div className="space-y-4">
          {/* Customer confirmation summary — pickup + release TIMES only.
              Deliberately no odometer readings anywhere on this screen. */}
          <div className="grid grid-cols-2 gap-2">
            {[
              { label: t('journeyPickup', { defaultValue: 'Pickup' }), value: pickupDisplay },
              { label: t('journeyRelease', { defaultValue: 'Release' }), value: releaseDisplay },
            ].map((cell) => (
              <div
                key={cell.label}
                style={{
                  padding: '12px 8px 14px',
                  background: 'var(--mint-soft)',
                  border: '1px solid var(--mint-line)',
                  borderRadius: 12,
                  textAlign: 'center',
                }}
              >
                <div className="font-bold uppercase mb-1" style={{ fontSize: 10, color: 'var(--green-700)', letterSpacing: '0.06em' }}>
                  {cell.label}
                </div>
                <div className="font-extrabold" style={{ fontSize: 20, color: 'var(--color-text)', fontVariantNumeric: 'tabular-nums' }}>
                  {cell.value}
                </div>
              </div>
            ))}
          </div>

          <div>
            <label className="block mb-2 text-sm font-medium text-[var(--color-text)]">
              {t('customer_signature', { defaultValue: 'Customer signature' })}
            </label>
            <SignaturePad
              value={signature}
              onChange={(dataUrl) => setSignature(dataUrl)}
            />
          </div>

          <div>
            <label className="block mb-3 text-sm font-medium text-[var(--color-text)] text-center">
              {t('customer_rating', { defaultValue: 'Customer rating' })}
            </label>
            <RatingStars value={rating} onChange={setRating} />
          </div>

          {/* In the release flow the duty-slip photo moved to the post-KM
              close-out (GH #503) so it's offered only after the final KM is in.
              Recapture has no close-out screen, so it stays here. */}
          {mode === 'recapture' && (
            <PhotoCapture
              value={dutySlipUrl}
              onChange={(dataUrl) => setDutySlipUrl(dataUrl)}
              label={t('slip_photo_optional', { defaultValue: 'Duty slip photo (optional)' })}
            />
          )}

          {error && <p className="text-sm text-red-600">{error}</p>}

          <Button onClick={handleSubmitClick} disabled={submitting} className="w-full">
            {t('submitDutySlip', { defaultValue: 'Submit Duty Slip' })}
          </Button>
        </div>
      </Sheet>

      {/* Skip modal — release mode only */}
      {skipOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="skip-modal-title"
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
        >
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setSkipOpen(false)}
            aria-hidden="true"
          />
          <div className="relative bg-[var(--color-surface,#fff)] rounded-2xl p-6 w-full max-w-sm shadow-2xl">
            <h3 id="skip-modal-title" className="text-base font-semibold text-[var(--color-text)] mb-2">
              {t('skipModalTitle', { defaultValue: 'Submit without signature?' })}
            </h3>
            <p className="text-sm text-[var(--color-text-secondary)] mb-4">
              {t('skipModalBody', {
                defaultValue:
                  'Tell us why — the passenger will still get a link to sign later.',
              })}
            </p>

            <div className="space-y-2 mb-4">
              {SKIP_REASONS.map((reason) => (
                <button
                  key={reason}
                  type="button"
                  onClick={() => setSkipReason(reason)}
                  className={[
                    'w-full text-left px-4 py-3 rounded-xl border text-sm font-medium transition-colors',
                    skipReason === reason
                      ? 'border-[var(--color-primary-500)] bg-[var(--color-primary-50)] text-[var(--color-primary-600)]'
                      : 'border-[var(--color-border)] text-[var(--color-text)]',
                  ].join(' ')}
                >
                  {reason === 'Passenger left without signing'
                    ? t('skipReasonLeft', { defaultValue: 'Passenger left without signing' })
                    : reason === 'In a hurry'
                    ? t('skipReasonHurry', { defaultValue: 'In a hurry' })
                    : t('skipReasonOther', { defaultValue: 'Other — type' })}
                </button>
              ))}

              {skipReason === 'Other — type' && (
                <input
                  type="text"
                  className="w-full border border-[var(--color-border)] rounded-xl px-4 py-2 text-sm mt-1 focus:outline-none focus:border-[var(--color-primary-500)]"
                  placeholder={t('skipReasonOtherPlaceholder', {
                    defaultValue: 'Describe the reason…',
                  })}
                  value={skipOtherText}
                  onChange={(e) => setSkipOtherText(e.target.value)}
                  autoFocus
                />
              )}
            </div>

            <div className="flex gap-3">
              <Button
                variant="secondary"
                onClick={() => setSkipOpen(false)}
                className="flex-1"
              >
                {t('skipKeepSigning', { defaultValue: 'Keep signing' })}
              </Button>
              <Button
                onClick={handleSkipConfirm}
                disabled={!canConfirmSkip || submitting}
                className="flex-1"
              >
                {t('skipConfirm', { defaultValue: 'Submit anyway' })}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
